import { Dropdown, Avatar } from "flowbite-react";
import { useState, useMemo } from "react";
import { logoutUser } from "@services/auth";
import { useNavigate } from "react-router-dom";
import { canSeeReactivation } from "@utils/access";

export { canSeeReactivation } from "@utils/access";

export const BASE_TABS = ["All Patients", "Form Library"];
export const REACTIVATION_TAB = "Reactivation";

export default function Navbar({
  selected,
  onSelect,
  showTabs = false,
  showFieldSearch = true,
  searchQuery = "",
  setSearchQuery = () => {},
}) {
  const navigate = useNavigate();
  const userEmail = localStorage.getItem("email");
  const [showSearch, setShowSearch] = useState(false);
  const isOwner = useMemo(() => {
    return localStorage.getItem("is_owner") === "true";
  }, []);
  const isAdmin = useMemo(() => {
    return localStorage.getItem("is_admin") === "true";
  }, []);
  const tabs = useMemo(
    () => (canSeeReactivation() ? [...BASE_TABS, REACTIVATION_TAB] : BASE_TABS),
    []
  );

  const handleLogout = () => logoutUser();

  const toggleSearch = () => {
    if (showSearch) setSearchQuery("");
    setShowSearch(!showSearch);
  };

  const renderTabs = () => (
    <div className="hidden lg:flex items-center mx-6 p-1.5 gap-1 bg-white/[0.06] rounded-xl backdrop-blur-sm border border-white/[0.08]">
      {tabs.map((tab) => (
        <button
          key={tab}
          onClick={() => (tab === REACTIVATION_TAB ? navigate("/reactivation") : onSelect(tab))}
          className={`flex items-center gap-2.5 px-5 py-2.5 text-sm rounded-lg transition-all duration-200 ${
            selected === tab
              ? "bg-gradient-to-b from-white/[0.15] to-white/[0.08] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_1px_3px_rgba(0,0,0,0.2)]"
              : "text-white/60 hover:text-white/90 hover:bg-white/[0.05]"
          }`}
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {renderTabIcon(tab, selected === tab)}
          {tab}
        </button>
      ))}
    </div>
  );

  const renderTabIcon = (tab, isActive) => {
    const iconClass = `w-5 h-5 transition-colors duration-200 ${isActive ? 'text-teal-400' : 'text-white/50'}`;
    if (tab === "All Patients") {
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24">
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.75"
            d="M4.5 17H4a1 1 0 0 1-1-1 3 3 0 0 1 3-3h1m0-3.05A2.5 2.5 0 1 1 9 5.5M19.5 17h.5a1 1 0 0 0 1-1 3 3 0 0 0-3-3h-1m0-3.05a2.5 2.5 0 1 0-2-4.45m.5 13.5h-7a1 1 0 0 1-1-1 3 3 0 0 1 3-3h3a3 3 0 0 1 3 3 1 1 0 0 1-1 1Zm-1-9.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Z"
          />
        </svg>
      );
    }
    if (tab === REACTIVATION_TAB) {
      return (
        <svg className={iconClass} fill="none" viewBox="0 0 24 24">
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.75"
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
      );
    }
    return (
      <svg className={iconClass} fill="none" viewBox="0 0 24 24">
        <path
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.75"
          d="M15 4h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3m0 3h6m-6 5h6m-6 4h6M10 3v4h4V3h-4Z"
        />
      </svg>
    );
  };

  const renderSearch = () => (
    <div className="flex items-center px-4 py-2.5 gap-3 bg-white/[0.08] border border-teal-500/40 rounded-xl shadow-lg shadow-teal-500/10 focus-within:border-teal-400/60 focus-within:bg-white/[0.1] transition-all duration-200 backdrop-blur-sm">
      <svg
        className="w-4 h-4 text-teal-400/80"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="m21 21-4.35-4.35M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
        />
      </svg>
      <input
        type="text"
        placeholder="Search patients, forms..."
        className="bg-transparent text-white placeholder-white/50 text-sm w-full sm:w-56 md:w-72 outline-none border-none focus:ring-0"
        style={{ fontFamily: 'var(--font-body)' }}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && searchQuery) {
            console.log("Searching for:", searchQuery);
          }
        }}
      />
    </div>
  );

  const renderSearchToggle = () => (
    <button
      onClick={toggleSearch}
      className="p-2.5 rounded-lg text-white/50 hover:text-teal-400 hover:bg-white/[0.06] transition-all duration-200"
      aria-label={showSearch ? "Close search" : "Open search"}
    >
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        {showSearch ? (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M6 18L18 6M6 6l12 12"
          />
        ) : (
          <path
            strokeLinecap="round"
            strokeWidth="2"
            d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
          />
        )}
      </svg>
    </button>
  );

  const renderUserDropdown = () => (
    <Dropdown
      inline
      label={
        <div className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/[0.06] transition-all duration-200 cursor-pointer group">
          <div className="relative">
            <Avatar
              img="/Klinifai-Icon-Transparent.png"
              alt="User avatar"
              rounded
              size="sm"
              placeholderInitials="MA"
              className="ring-2 ring-white/10 group-hover:ring-teal-400/30 transition-all duration-200"
            />
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#0a0f1c]"></div>
          </div>
          <div className="hidden sm:flex flex-col items-start">
            <span className="text-white/90 text-sm font-medium" style={{ fontFamily: 'var(--font-display)' }}>
              My Account
            </span>
            <span className="text-white/40 text-xs truncate max-w-[120px]">
              {userEmail}
            </span>
          </div>
        </div>
      }
    >
      <Dropdown.Item disabled>
        <div className="flex flex-col items-start leading-tight">
          <span className="font-medium text-slate-800" style={{ fontFamily: 'var(--font-display)' }}>Account</span>
          <span className="text-xs text-slate-500">{userEmail}</span>
        </div>
      </Dropdown.Item>
      {isOwner && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => navigate("/users-permissions")} className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            Users & Permissions
          </Dropdown.Item>
          <Dropdown.Item onClick={() => navigate("/audit-logs")} className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Audit Logs
          </Dropdown.Item>
        </>
      )}
      {isAdmin && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => navigate("/admin/clinics")} className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Clinics
          </Dropdown.Item>
          <Dropdown.Item onClick={() => navigate("/admin/agent-questions")} className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Agent Questions
          </Dropdown.Item>
          <Dropdown.Item onClick={() => navigate("/admin/agent-tests")} className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Agent Tests
          </Dropdown.Item>
          <Dropdown.Item onClick={() => navigate("/admin/call-matching")} className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Call Matching
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => navigate("/sms-leads")}>
            SMS Leads
          </Dropdown.Item>
        </>
      )}
      {/* Pages such as the frontdesk surface hide the centre tabs, so the
          menu keeps an entry to Reactivation under the same gate. */}
      {canSeeReactivation() && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => navigate("/reactivation")}>
            Patient Reactivation
          </Dropdown.Item>
        </>
      )}
      <Dropdown.Divider />
      <Dropdown.Item onClick={handleLogout} className="flex items-center gap-2 text-red-600 hover:text-red-700">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        Sign Out
      </Dropdown.Item>
    </Dropdown>
  );

  return (
    <nav className="navbar-premium text-white px-5 py-3.5 flex items-center justify-between relative">
      {/* Subtle top highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

      {/* Left: Logo */}
      <div className="flex items-center gap-3">
        <div className="relative group">
          <div className="absolute -inset-2 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <img
            src="/Klinifai_Logo_Horizontal_Dark_Transparent.svg"
            alt="logo"
            className="w-32 h-auto relative"
          />
        </div>
      </div>

      {/* Center: Tabs */}
      {showTabs && renderTabs()}

      {/* Right: Search + Avatar */}
      <div className="flex items-center gap-2 md:gap-4">
        {showFieldSearch && showSearch && renderSearch()}
        {showFieldSearch && renderSearchToggle()}
        {renderUserDropdown()}
      </div>
    </nav>
  );
}
